/**
 * 🔱 **What the spear can reach, and what it is pointed at** — T221, D115.
 *
 * 🎯 **haugur chose the verb:** *"Thrust — jab at a fish within reach."* You get
 * close, aim, and stab. **No projectile, nothing to retrieve, nothing to lose.**
 *
 * ## 🔴 WHY THIS IS IN `shared/` AND NOT IN THE CLIENT
 *
 * **Both sides run it, on the same numbers.** The client uses it to decide what
 * the crosshair is on and to name that fish; **the server uses it to decide
 * whether that name was honest**, against its own copy of where everything is.
 *
 * 🔴 **The client naming a fish is exactly as safe as the browser naming a
 * player**, which this project already settled: *"the browser never sends a
 * name, it sends a credential"* — **a client that can name its own target can
 * name a fish on the other side of the bay.** ⚠️ So the id on the wire is a
 * *proposal*, and this function is the answer. **Two copies of the rule would
 * eventually disagree, and the disagreement would look like lag.**
 *
 * 👉 `fishAt` lived in `shared/` for precisely this reason before D117 deleted
 * it. **The reason outlived the function.**
 */

import { fishByKey } from './fish.ts';
import { type AimTarget, aimAt } from './aim.ts';
import type { Vec3 } from './creature.ts';

/**
 * 🔱 **How far a thrust reaches, in metres.**
 *
 * ⚠️ **SHORTER THAN YOUR ARM REACHES A BLOCK, WHICH IS 6 m**, and that is the
 * point rather than an oversight: **D117 makes a fish wary but catchable — *"it
 * settles if you hold still, so the skill is patience"*** — and a reach as long
 * as the block reach would let you take one from wherever you happened to stop.
 *
 * 🔴 **A DIAL, AND THE PROJECT MANAGER'S RATHER THAN HIS.** ⚠️ **And it is
 * flattered by something haugur parked:** water does nothing to the player
 * (**T229**), so you wade at walking pace and can simply stroll up to a fish.
 * **Until that changes, this number is doing the whole job of making a catch
 * cost something**, which is more weight than one number should carry.
 */
export const SPEAR_REACH = 3.5;

/**
 * 🔱 **The item you must be holding.**
 *
 * ⚠️ **Here rather than in the server**, because the CLIENT needs it too — it
 * decides whether a click is a thrust or an ordinary swing at a block, and a
 * second copy of the key is a second thing to rename.
 */
export const SPEAR_ITEM = 'spjot';

/**
 * ⚠️ **How forgiving the aim is, as a share of the fish's own length.**
 *
 * **A bigger fish is a bigger target**, which needs no second number and is what
 * a person would expect: a 1.08 m lax is easier to hit than a 0.48 m bleikja,
 * and the lax is the one worth 3 food. 🔴 **Fixed radii were the alternative and
 * they hide a decision** — one number that suits a cod is generous for a char
 * and mean for a salmon.
 */
const AIM_RADIUS_OF_LENGTH = 0.5;

/**
 * Anything the spear could be pointed at. The wire's `creatures` shape.
 *
 * ⚠️ **The same shape {@link AimTarget} describes**, and an alias rather than a
 * second declaration — two identical interfaces drift the day somebody adds a
 * field to one of them.
 */
export type SpearTarget = AimTarget;

/**
 * 🔱 **Which fish this thrust would take, or `undefined` for a miss.**
 *
 * `eye` is the camera, `look` a unit vector along the crosshair.
 *
 * ## The rule
 *
 * A fish counts if it is **in front of you**, **within {@link SPEAR_REACH}**,
 * and the crosshair passes **within half its own length** of it. Of everything
 * that qualifies, **the nearest wins** — a fish behind another cannot be taken
 * through it.
 *
 * ⚠️ **`y` on the wire is the FEET**, the same convention players use. A fish is
 * a hand's breadth tall, and the aim radius is at least 0.24 m, so no correction
 * is applied — **said here rather than left for somebody to wonder about.**
 *
 * 🔴 **Anything that is not one of the four fish is ignored**, rather than being
 * spearable by default. **The day a sheep exists, it must not become catchable
 * because nobody remembered this function.**
 */
export function spearTargetFrom(
  eye: Vec3,
  look: Vec3,
  targets: readonly SpearTarget[],
  reach: number = SPEAR_REACH,
): SpearTarget | undefined {
  /* 🎯 The geometry lives in `aim.ts`, once — see its header. 🔴 **What stays
     HERE is the only part a spear and a pair of hands disagree about: WHICH
     creatures are candidates, and how big a target each one is.** A fish that
     is not one of the four scores no radius and is therefore never returned,
     which is this function's oldest promise: *the day a sheep exists, it must
     not become catchable because nobody remembered this function.* */
  return aimAt(eye, look, targets, reach, (t) => {
    const species = fishByKey(t.kind);
    if (species === undefined) return 0;
    return species.length * AIM_RADIUS_OF_LENGTH;
  });
}

/**
 * 👁️ **Which way a player is looking, as a unit vector** — from the yaw and
 * pitch the client already sends in `player_state`.
 *
 * ## 🔴 Why this is here and not in the client's `movement.ts`
 *
 * **It was in the client**, as `lookDirection`, derived by negating
 * `cameraOffset` — and that is still where the client gets it. ⚠️ **But the
 * SERVER now needs the same answer**, to check that a speared fish was actually
 * in front of the player, and a server that re-derived it would be a **second
 * copy of a sign convention**.
 *
 * 🔴 **This repo has already paid for exactly that mistake.** `fishshot.mjs`
 * turned its camera by the PLAYER's yaw convention when a fish's is **a quarter
 * turn apart**, photographed every fish nose-on, and produced *"a bug report
 * about the game, written by the camera"* — an afternoon spent on a fish that
 * was never thin. **Two copies of an angle convention is how that happens.**
 *
 * ✅ So the arithmetic lives once, here, and `movement.ts` calls it.
 */
export function lookVector(yaw: number, pitch: number): Vec3 {
  const cosPitch = Math.cos(pitch);
  /* ⚠️ The NEGATION is the whole subtlety. `cameraOffset` points from the
     player back to the camera, which sits BEHIND them; looking is the other
     way. A sign error here would spear the fish behind your head, which reads
     as broken aiming rather than as a minus sign. */
  return {
    x: -Math.sin(yaw) * cosPitch,
    y: Math.sin(pitch),
    z: -Math.cos(yaw) * cosPitch,
  };
}
